import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { login as loginService } from '../../services/authService';
import CoreSellLogo from '../../components/CoreSellLogo';

// آیکون‌های SVG بومی
const PhoneIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ViewIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ViewOffIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { login: setAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await loginService(data.phone, data.password);
      // ساختار پاسخ واقعی: { data: { user, token, ... }, message? }
      const userData = response.data || response.user || response;
      const token = userData.token || response.token;

      if (!token) {
        throw new Error('توکن احراز هویت از سرور دریافت نشد');
      }

      setAuth(userData, token);
      // AuthContext خودش toast موفقیت را نشان می‌دهد
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'خطا در ورود');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      
      
      <div dir="rtl" className="min-h-screen flex bg-white text-slate-900 font-sans">
        
        {/* ========================================================= */}
        {/* 1. بخش راست (فرم ورود) */}
        {/* ========================================================= */}
        <div className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-24 py-12 relative">
          <div className="w-full max-w-[400px]">
            
            {/* لوگوی موبایل (فقط در صفحات کوچک) */}
            <div className="flex lg:hidden items-center mb-8">
              <CoreSellLogo to="/" variant="dark" size="md" />
            </div>

            <div className="space-y-2 mb-10">
              <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
                ورود به حساب
              </h1>
              <p className="text-slate-500">
                برای مدیریت قدرتمند انبار آنلاین خود وارد شوید.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              
              {/* فیلد شماره موبایل */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-800">شماره موبایل</label>
                <div className="relative">
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <PhoneIcon className="w-4 h-4" />
                  </div>
                  <input
                    type="tel"
                    placeholder="09123456789"
                    dir="ltr"
                    className={`w-full bg-slate-50 border ${errors.phone ? 'border-red-500' : 'border-slate-200'} rounded-xl py-3.5 pr-11 pl-4 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all`}
                    {...register('phone', {
                      required: 'شماره موبایل الزامی است',
                      pattern: {
                        value: /^09\d{9}$/,
                        message: 'فرمت شماره موبایل صحیح نیست',
                      },
                    })}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.phone.message}</p>
                )}
              </div>

              {/* فیلد رمز عبور */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-slate-800">رمز عبور</label>
                <div className="relative">
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <LockIcon className="w-4 h-4" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    dir="ltr"
                    className={`w-full bg-slate-50 border ${errors.password ? 'border-red-500' : 'border-slate-200'} rounded-xl py-3.5 pr-11 pl-12 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all`}
                    {...register('password', {
                      required: 'رمز عبور الزامی است',
                      minLength: {
                        value: 6,
                        message: 'حداقل ۶ کاراکتر الزامی است',
                      },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 p-1 focus:outline-none"
                  >
                    {showPassword ? <ViewOffIcon className="w-5 h-5" /> : <ViewIcon className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.password.message}</p>
                )}
              </div>

              {/* دکمه ورود */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-xl text-base font-bold shadow-[0_10px_20px_-5px_rgba(59,130,246,0.4)] hover:shadow-[0_15px_25px_-5px_rgba(59,130,246,0.5)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-md transition-all flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none mt-2"
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'ورود به پنل کاربری'
                )}
              </button>

            </form>

            <p className="text-sm text-slate-400 text-center mt-8">
              توسعه داده شده با ❤️ برای فروشندگان آنلاین
            </p>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 2. بخش چپ (گرافیک و برندینگ - فقط در دسکتاپ) */}
        {/* ========================================================= */}
        <div className="hidden lg:flex flex-1 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden items-center justify-center">
          
          {/* افکت‌های نوری پس‌زمینه */}
          <div className="absolute -top-[10%] -left-[10%] w-[500px] h-[500px] bg-blue-500 blur-[120px] opacity-40 rounded-full mix-blend-screen"></div>
          <div className="absolute -bottom-[10%] -right-[10%] w-[500px] h-[500px] bg-cyan-500 blur-[120px] opacity-20 rounded-full mix-blend-screen"></div>

          {/* محتوای بصری */}
          <div className="relative z-10 max-w-md px-8 flex flex-col space-y-8">
            
            {/* لوگوی Abstract */}
            <div className="flex items-center gap-4">
              <CoreSellLogo to="/" variant="light" size="lg" />
            </div>

            <h2 className="text-white text-4xl leading-[1.4] font-bold">
              مرکز فرماندهی فروش شما در تمام کانال‌ها
            </h2>
            
            <p className="text-blue-100 text-lg leading-relaxed opacity-80">
              هسته‌ی مرکزی کسب‌وکار خود را با کورسل مدیریت کنید. موجودی دیجی‌کالا، باسلام و سایت شخصی شما همیشه یکپارچه و دقیق خواهد بود.
            </p>

            {/* کامپوننت تزئینی */}
            <div className="mt-8 p-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-400 text-white flex items-center justify-center shadow-[0_0_20px_rgba(74,222,128,0.4)]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">سیستم آماده به کار</h3>
                  <p className="text-white/70 text-sm">تمام درگاه‌ها متصل هستند</p>
                </div>
              </div>
            </div>

          </div>
        </div>
        
      </div>
    </>
  );
}